import { useCallback, useMemo } from "react";
import { MAX_PERCENTAGE, generatePercentageSliderSchema } from "Utils";
import * as yup from "Utils/yup";

type PercentageValue = number | null;

const DEFAULT_PERCENTAGE = 50;

const getRemainingPercentageValueFromInput = (
  inputValue: string,
): PercentageValue => {
  if (!inputValue) {
    return null;
  }
  const value = +inputValue;
  return isNaN(value) ? null : MAX_PERCENTAGE - value;
};

type UsePercentageSlider<TForm extends Record<string, PercentageValue>> = {
  firstPercentageName: keyof TForm;
  secondPercentageName: keyof TForm;
  defaultSliderValue?: number;
  firstPercentageMin?: number;
  firstPercentageMax?: number;
  secondPercentageMin?: number;
  secondPercentageMax?: number;
};

const usePercentageSlider = <TForm extends Record<string, PercentageValue>>({
  firstPercentageName,
  secondPercentageName,
  defaultSliderValue = DEFAULT_PERCENTAGE,
  firstPercentageMin,
  firstPercentageMax,
  secondPercentageMin,
  secondPercentageMax,
}: UsePercentageSlider<TForm>) => {
  const schema: yup.SchemaOf<TForm> = useMemo(
    () =>
      generatePercentageSliderSchema<TForm>({
        firstPercentageName,
        secondPercentageName,
        firstPercentageMin,
        firstPercentageMax,
        secondPercentageMin,
        secondPercentageMax,
      }),
    [
      firstPercentageName,
      secondPercentageName,
      firstPercentageMin,
      firstPercentageMax,
      secondPercentageMin,
      secondPercentageMax,
    ],
  );

  const formDefaultValues = useMemo(
    () => ({
      [firstPercentageName]: defaultSliderValue,
      [secondPercentageName]: MAX_PERCENTAGE - defaultSliderValue,
    }),
    [firstPercentageName, secondPercentageName, defaultSliderValue],
  );

  const getSliderPercentages = useCallback(
    (
      firstPercentageValue: PercentageValue | "",
    ): {
      firstPercentage: PercentageValue;
      secondPercentage: PercentageValue;
    } => {
      if (
        firstPercentageValue === "" ||
        firstPercentageValue === null ||
        isNaN(+firstPercentageValue)
      ) {
        return { firstPercentage: null, secondPercentage: null };
      }

      const firstPercentage = +firstPercentageValue;
      const secondPercentage = MAX_PERCENTAGE - firstPercentage;

      return { firstPercentage, secondPercentage };
    },
    [],
  );

  const onChangePercentage = useCallback(
    (
      value: number,
      setValue: (
        name: keyof TForm,
        value: PercentageValue,
        options: { [k: string]: boolean },
      ) => void,
    ) => {
      const { firstPercentage, secondPercentage } = getSliderPercentages(value);

      setValue(firstPercentageName, firstPercentage, {
        shouldDirty: true,
      });
      setValue(secondPercentageName, secondPercentage, {
        shouldDirty: true,
      });
    },
    [firstPercentageName, secondPercentageName, getSliderPercentages],
  );

  return {
    schema,
    formDefaultValues,
    getSliderPercentages,
    getRemainingPercentageValueFromInput,
    onChangePercentage,
  };
};

export default usePercentageSlider;
